import { pageContentClass } from "../../store/slice/page-layout";
import { memo, useEffect } from "react";
import { useState } from "react";
import HeaderNavbar from "../../components/header/header";
import FaIcon from "../../components/utils/fa_icon";
import { faChevronLeft, faAddressCard } from "@fortawesome/free-solid-svg-icons";
import SideMenu from "../../components/sidemenu/sidemenu";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  useApiService,
  ArtDialog,
  ArtButton,
  AdkProvider,
} from "@art/shared-ui";
import { Box, Button, Card } from "@mui/material";
import { useIDPContext } from "../../auth";
import { useDispatch, useSelector } from "react-redux";
import ArtStore, { RootState, updateLicenseState } from "../../store";
import classNames from "classnames";
import AppSwitcher from "../../components/header/app-switch/app-switcher";
import {
  getCategoryForPath,
  getVisibleSettingsCategories,
} from "../../components/header/settings-menu";
import MobileMenuDropdown from "../sidemenu/MobileMenuDropdown";
import SidebarTooltip from "../sidemenu/sidebar_tooltip";
import SettingsBackLink, {
  useTrackSettingsReturnPath,
} from "../sidemenu/settings-back-link";
import {
  buildPaymentAttemptStorageKey,
  clearPaymentAttemptKey,
  getPaymentAttemptKey,
  OrderSummaryDialog,
  PaymentSuccessDialog,
  UpgradeRestoreSelectionDialog,
} from "../../global/settings/billing-settings/helpers";
import { usePermissionsContext } from "../../global/permissions";
import { licenseStateFromProfile } from "../../utils/license";
import { applicationBrandForPath } from "../../utils/application-brand";
import PlaygroundRuntimeLoader from "../../global/remote-loaders/playground-runtime-loader";
import SubscriptionBanner from "./subscription-banner";
import ArtTrixyGuide from "../coach-mark/trixy-guide";

type UpgradeStep = "plans" | "checkout" | "billing" | "upgrade_restore" | null;

const AdminLayout = memo(() => {
  const [isToggled, setIsToggled] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 991px)").matches
      : false,
  );
  const [menuItems, setMenuItems]: any = useState([]);
  const {
    has: hasPermission,
    hasAttribute,
    permissions,
    isSuperAdmin,
  } = usePermissionsContext();
  const location = useLocation();
  const navigate = useNavigate();
  const visibleSettingsCategories = getVisibleSettingsCategories(hasPermission);
  useTrackSettingsReturnPath();
  const pageLayout = useSelector((state: RootState) => state.pageLayout);
  const appState = useSelector((state: RootState) => state.appstate);
  const { licenseConfig } = useSelector(
    (state: RootState) => state.license,
  );

  const authState = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { authGuardInstance } = useIDPContext() as { authGuardInstance: any };

  const apiservice = useApiService();

  // Upgrade dialog state
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null);
  const [, setSubscriptionData] = useState<any>(null);
  const [upgradeStep, setUpgradeStep] = useState<UpgradeStep>(null);
  const [, setIsPaying] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [restoreData, setRestoreData] = useState<any>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const userRole = authGuardInstance?.user?.tenant?.roles;
  const tenantName = authState.tenantName;

  const currentPlan = licenseConfig?.license_plan_code?.toLowerCase();
  const canUpgradePlan =
    hasAttribute("PLATFORM_SETTINGS", "BILLING_USAGE", "ViewBilling") &&
    hasAttribute("PLATFORM_SETTINGS", "BILLING_USAGE", "ChangeSubscription") &&
    hasAttribute("PLATFORM_SETTINGS", "BILLING_USAGE", "ManagePayments");

  // const handleUpgradeClick = () => {
  //   if (!canUpgradePlan) return;
  //   setUpgradeDialogOpen(true);
  //   setUpgradeStep("plans");
  //   setSelectedPlan(null);
  //   setSubscriptionData(null);
  //   setPaymentId("");
  // };
  const handleUpgradeClick = () => {
    if (!canUpgradePlan) return;
    navigate("/platform-settings/billing/overview");
  };

  const handleUpgradeDialogClose = () => {
    setUpgradeDialogOpen(false);
    setUpgradeStep(null);
    setSelectedPlan(null);
    setSubscriptionData(null);
    setIsPaying(false);
    setPaymentId("");
  };

  const buildSubscriptionPaymentAttemptKey = (plan: any) =>
    buildPaymentAttemptStorageKey(
      "subscription",
      tenantName || "",
      plan?.type || "",
      plan?.priceId || "",
    );

  const initiateSubscriptionPayment = async (
    payload: any,
    storageKey: string,
    retry = true,
  ) => {
    const paymentAttemptKey = getPaymentAttemptKey(storageKey);

    try {
      return await apiservice.post(
        `/payment/subscription/initiate`,
        { ...payload, idempotency_key: paymentAttemptKey },
        {
          headers: {
            "Idempotency-Key": paymentAttemptKey,
          },
        },
      );
    } catch (error: any) {
      const data = error?.response?.data || {};
      const errorCode = data.error_code || data.code || data.errorCode;

      if (retry && errorCode === "PAYMENT_ATTEMPT_EXPIRED") {
        clearPaymentAttemptKey(storageKey);
        return initiateSubscriptionPayment(payload, storageKey, false);
      }

      if (retry && errorCode === "IDEMPOTENCY_KEY_REQUIRED") {
        clearPaymentAttemptKey(storageKey);
        return initiateSubscriptionPayment(payload, storageKey, false);
      }

      if (errorCode === "PAYMENT_ATTEMPT_CLOSED") {
        clearPaymentAttemptKey(storageKey);
        apiservice.showSnack(
          "This payment attempt has been closed. Please start again.",
          "error",
        );
      }

      if (errorCode === "IDEMPOTENCY_KEY_REUSED") {
        clearPaymentAttemptKey(storageKey);
        apiservice.showSnack(
          "The payment attempt key was reused. Please start a fresh payment attempt.",
          "error",
        );
      }

      throw error;
    }
  };

  const cancelPaymentAttempt = (orderId?: string, reason?: string) => {
    if (!orderId) return;
    apiservice
      .post("/payment/subscription/attempt/cancel", {
        razorpay_order_id: orderId,
        reason,
      })
      .catch((err: any) => {
        console.error("Failed to record payment attempt cancellation:", err);
      });
  };

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const refreshLicenseState = async () => {
    try {
      const {
        data: { data },
      } = await apiservice.get("/user/profile");

      dispatch(updateLicenseState(licenseStateFromProfile(data)));
    } catch (error) {
      console.error("Failed to refresh license state:", error);
    }
  };

  const verifyOrderPayment = async (response: any, activePlan: any) => {
    try {
      const verifyPayload = {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      };

      const verifyResponse = await apiservice.post(
        `payment/subscription/verify`,
        verifyPayload,
      );

      if (verifyResponse?.status === 200 || verifyResponse?.status === 201) {
        clearPaymentAttemptKey(
          buildPaymentAttemptStorageKey(
            "subscription",
            tenantName || "",
            activePlan?.type || "",
            activePlan?.priceId || "",
          ),
        );

        await refreshLicenseState();
        setPaymentId(response.razorpay_payment_id || "");

        // Selection-driven restore: open the dialog whenever the tenant has
        // anything in the downgraded state. Falls through to the success
        // screen if nothing's pending.
        try {
          const restoreResp = await apiservice.get("/upgrade/restore-status");
          const data = { ...restoreResp?.data?.data, projects: [] };
          const hasItems = (data?.channels?.length ?? 0) > 0;
          if (hasItems) {
            setRestoreData(data);
            setUpgradeStep("upgrade_restore");
            return;
          }
        } catch (err: any) {
          console.error(
            "[upgrade-restore] /upgrade/restore-status failed:",
            err,
          );
        }

        setUpgradeStep("billing");
      }
    } catch (verifyError: any) {
      console.error("Order payment verification failed:", verifyError);
      const data = verifyError?.response?.data || {};
      apiservice.showSnack(
        data.message || "Payment verification failed. Please contact support.",
        "error",
      );
    } finally {
      setIsPaying(false);
    }
  };

  const submitUpgradeRestore = async (
    channelIds: string[],
  ) => {
    setIsRestoring(true);
    try {
      await apiservice.post("/upgrade/restore-resources", {
        channel_ids: channelIds,
      });
      setUpgradeStep("billing");
    } catch (err: any) {
      apiservice.showSnack(
        err?.response?.data?.message ||
          "Failed to restore the selected resources. Please try again.",
        "error",
      );
    } finally {
      setIsRestoring(false);
    }
  };

  const skipUpgradeRestore = () => {
    setUpgradeStep("billing");
  };

  const handleRazorpayPayment = async (
    plan: any,
    _existingPreview?: any,
    billingAddress?: any,
  ) => {
    if (!canUpgradePlan) return;
    setIsPaying(true);
    const activePlan = plan || selectedPlan;

    try {
      const storageKey = buildSubscriptionPaymentAttemptKey(activePlan);
      const subscriptionPayload = {
        license_plan_code: activePlan?.type,
        pricing_id: activePlan?.priceId,
        email: authState.user?.email,
        contact: billingAddress?.contact,
      };

      const subscriptionResponse = await initiateSubscriptionPayment(
        subscriptionPayload,
        storageKey,
      );

      const paymentData = subscriptionResponse?.data?.data;
      if (!paymentData?.order_id) {
        apiservice.showSnack("Failed to create payment order", "error");
        setIsPaying(false);
        return;
      }

      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        apiservice.showSnack(
          "Failed to load payment gateway. Please try again.",
          "error",
        );
        setIsPaying(false);
        return;
      }

      const options = {
        key: paymentData.razorpay_key_id,
        name: "ART Console",
        description: paymentData.is_upgrade
          ? `Upgrade to ${activePlan?.planTitle}`
          : `Subscribe to ${activePlan?.planTitle}`,
        order_id: paymentData.order_id,
        customer_id: paymentData.customer_id,
        recurring: 1,
        prefill: {
          name: authState.user?.first_name || "",
          email: authState.user?.email || "",
        },
        theme: { color: "#1976d2" },
        modal: {
          ondismiss: () => {
            setIsPaying(false);
            apiservice.showSnack("Payment cancelled", "info");
            cancelPaymentAttempt(
              paymentData.order_id,
              "user_dismissed_checkout",
            );
          },
        },
        handler: async (response: any) => {
          await verifyOrderPayment(response, activePlan);
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      console.error("Error initiating payment:", error);
      apiservice.showSnack(
        error?.response?.data?.message ||
          "Failed to initiate payment, Please try again.",
        "error",
      );
      setIsPaying(false);
    }
  };

  useEffect(() => {
    if (
      location.pathname.startsWith("/application-settings") ||
      location.pathname.startsWith("/account-settings") ||
      location.pathname.startsWith("/platform-settings")
    ) {
      loadSettingMenu();
    } else if (location.pathname.startsWith("/live")) {
      loadRemoteMenu(import("art_liveconnect/utils"));
    } else if (location.pathname.startsWith("/agent-builder")) {
      loadRemoteMenu(import("art_agentlab/agent_utils"));
    } else if (location.pathname.startsWith("/app-studio")) {
      loadRemoteMenu(import("art_appstudio/app_studio_utils"));
    } else if (location.pathname.startsWith("/datalake")) {
      loadRemoteMenu(import("art_datalake/utils"));
    } else if (location.pathname.startsWith("/super-admin")) {
      loadRemoteMenu(import("art_superadmin/super_admin_utils"));
    } else {
      setMenuItems([]);
    }
  }, [location.pathname, appState.force_fullscreen, permissions, isSuperAdmin]);

  useEffect(() => {
    const compactViewport = window.matchMedia("(max-width: 991px)");
    const syncSidebarToViewport = (event: MediaQueryListEvent) => {
      setIsToggled(event.matches);
    };

    compactViewport.addEventListener("change", syncSidebarToViewport);
    return () => compactViewport.removeEventListener("change", syncSidebarToViewport);
  }, []);

  useEffect(() => {
    if (window.matchMedia("(max-width: 991px)").matches) {
      setIsToggled(true);
    }
  }, [location.pathname]);

  const logoProps = applicationBrandForPath(location.pathname).logo;

  const loadRemoteMenu = async (remote: Promise<any>) => {
    try {
      const { getMenu } = await remote;

      const items = getMenu(hasPermission, isSuperAdmin);
      setMenuItems(items);
    } catch (e) {
      setMenuItems([]);
    }
  };

  const loadSettingMenu = async () => {
    const category = getCategoryForPath(location.pathname);
    const items = category.items
      .filter((item) => !item.permission || item.permission(hasPermission))
      .map(({ title, icon, href, activeMatch }) => ({
        title,
        icon,
        href,
        activeMatch,
      }));
    setMenuItems(items);
  };

  const handleBackdropClick = () => {
    setIsToggled(true);
  };

  return (
    <AdkProvider ArtStore={ArtStore} Uri={import.meta.env.PUBLIC_WS_URL}>
      <PlaygroundRuntimeLoader />
      <ArtTrixyGuide />
      <div>
        <main
          className={classNames("art_main_wrapper", {
            art_main_wrapper_fullscreen: appState.force_fullscreen,
            art_main_wrapper_collapsed: isToggled || appState.force_fullscreen,
          })}
        >
          <div className="art_sidebar_backdrop" onClick={handleBackdropClick} />
          <button
            type="button"
            className="art_sidebar_toggle_button"
            data-qa="side-menu-toggle"
            aria-label={isToggled ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!isToggled}
            aria-controls="art-sidebar"
            onClick={() => setIsToggled((current) => !current)}
          >
            <FaIcon className="icon" icon={faChevronLeft} />
          </button>
          <div
            id="art-sidebar"
            className="art_sidebar art_card d-flex flex-column"
            data-qa="side-menu"
          >
            <div className="art_sidebar_header" data-qa="side-menu-header">
              <div className="art_desktop_app_launcher">
                <AppSwitcher
                  userRole={userRole}
                  renderTrigger={({ open, isOpen, activeApp }) => (
                    <button
                      type="button"
                      className={classNames("art_app_launcher_button", {
                        active: isOpen,
                      })}
                      aria-label={
                        activeApp
                          ? `Open application launcher. Current application: ${activeApp.title}`
                          : "Open application launcher"
                      }
                      aria-haspopup="dialog"
                      aria-expanded={isOpen}
                      title="Applications"
                      data-qa="sidebar-product-switcher"
                      onClick={open}
                    >
                      <ApplicationSwitcherIcon />
                    </button>
                  )}
                />
              </div>
              <div className="art_app_logo">
                <img
                  src={logoProps.src}
                  width={153}
                  height={59}
                  className="art_app_logo_full"
                  alt={logoProps.alt}
                  draggable="false"
                />
                <img
                  src={logoProps.short}
                  width={59}
                  height={59}
                  className="art_app_logo_short"
                  alt={logoProps.alt}
                  draggable="false"
                />
              </div>
            </div>

            <div className="sidepanel_content" data-qa="side-menu-content">
              <SideMenu
                isToggled={isToggled || appState.force_fullscreen}
                menuItems={menuItems ?? []}
              />
            </div>
            <div className="mt-auto">
              <SettingsBackLink
                isToggled={isToggled || appState.force_fullscreen}
              />
              <div className="art_mobile_actions">
                <Card className="art_mobile_card">
                  <div className="art_mobile_menu_container">
                    <AppSwitcher
                      userRole={userRole}
                      renderTrigger={({ open }) => (
                        <SidebarTooltip
                          title="Products"
                          placement="right"
                          disableHoverListener={
                            !(isToggled || appState.force_fullscreen)
                          }
                        >
                          <div
                            role="button"
                            tabIndex={0}
                            className={`art_mobile_menu_item ${
                              isToggled || appState.force_fullscreen
                                ? "toggled"
                                : ""
                            }`}
                            title="Switch Product"
                            data-qa="mobile-product-switcher"
                            onClick={() => {
                              open();
                              setIsToggled(true);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                open();
                                setIsToggled(true);
                              }
                            }}
                          >
                            <ApplicationSwitcherIcon mobile />
                            {!(isToggled || appState.force_fullscreen) && (
                              <span className="art_mobile_label">Products</span>
                            )}
                          </div>
                        </SidebarTooltip>
                      )}
                    />

                    <MobileMenuDropdown
                      icon={faAddressCard}
                      label="Profile"
                      title="User Profile"
                      menuItems={visibleSettingsCategories.map((category) => ({
                        title: category.label,
                        to: category.defaultHref,
                        key: category.key,
                        dataQa: `settings-${category.key}`,
                      }))}
                      isToggled={isToggled || appState.force_fullscreen}
                      userEmail={authState.user?.email}
                      profileUser={authState.user}
                      tenantName={authState.tenantName}
                      settingsCategories={visibleSettingsCategories}
                      onClose={() => setIsToggled(true)}
                    />
                  </div>
                </Card>
              </div>

              {!(isToggled || appState.force_fullscreen) &&
                currentPlan?.endsWith("sandbox") && canUpgradePlan && (
                  <Card className="art_dashboard_card d-flex flex-column align-items-center justify-content-center gap-2">
                    <Button
                      variant="contained"
                      sx={{ width: "100%" }}
                      color="primary"
                      onClick={handleUpgradeClick}
                    >
                      Upgrade Plan
                    </Button>
                  </Card>
                )}
            </div>
          </div>
          <div className="art_content">
            <div className="art_navbar">
              <HeaderNavbar />
            </div>
            <SubscriptionBanner />
            <div className={pageContentClass(pageLayout, location.key)}>
              <Outlet />
            </div>
          </div>
        </main>

        {/* Upgrade Plan Dialog */}
        <ArtDialog
          open={upgradeDialogOpen && canUpgradePlan}
          onClose={handleUpgradeDialogClose}
          title={
            upgradeStep === "checkout"
              ? "Complete Your Purchase"
              : upgradeStep === "billing"
                ? "Payment Successful"
                : upgradeStep === "upgrade_restore"
                  ? "Restore your channels"
                  : "Upgrade Your Plan"
          }
          size={upgradeStep === "billing" ? "medium" : "large"}
          type="form"
          actionBuilder={() => {
            const hasOwnActions =
              upgradeStep === "checkout" ||
              upgradeStep === "billing" ||
              upgradeStep === "upgrade_restore";

            return hasOwnActions ? (
              <></>
            ) : (
              <ArtButton
                buttonType="button"
                dataQa="btn-close-upgrade-dialog"
                label="Close"
                variant="outlined"
                color="error"
                onClick={handleUpgradeDialogClose}
              />
            );
          }}
        >
          {upgradeStep === "checkout" && selectedPlan ? (
            <OrderSummaryDialog
              selectedPlan={selectedPlan}
              currentPlan={licenseConfig}
              onProceedToPayment={(
                plan: any,
                preview: any,
                billingAddress: any,
              ) => handleRazorpayPayment(plan, preview, billingAddress)}
              onBack={() => setUpgradeStep("plans")}
            />
          ) : upgradeStep === "upgrade_restore" ? (
            <UpgradeRestoreSelectionDialog
              restoreData={restoreData}
              isSubmitting={isRestoring}
              onSkip={skipUpgradeRestore}
              onConfirm={(_projectIds: string[], channelIds: string[]) =>
                submitUpgradeRestore(channelIds)
              }
            />
          ) : upgradeStep === "billing" && selectedPlan ? (
            <PaymentSuccessDialog
              selectedPlan={selectedPlan}
              paymentId={paymentId}
              onClose={handleUpgradeDialogClose}
            />
          ) : <Box>Choose a product plan from Billing overview.</Box>}
        </ArtDialog>
      </div>
    </AdkProvider>
  );
});

function ApplicationSwitcherIcon({ mobile = false }: { mobile?: boolean }) {
  return (
    <span
      className={classNames("art_app_switch_icon", {
        art_mobile_icon: mobile,
      })}
      aria-hidden="true"
    >
      <svg
        className="art_app_switch_svg"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          className="art_app_switch_window"
          x="10.25"
          y="3.25"
          width="18.5"
          height="15.5"
          rx="2.75"
        />
        <path d="M10.5 8.25H28.5" />
        <circle cx="13.25" cy="5.75" r="0.85" fill="currentColor" />
        <circle cx="16.25" cy="5.75" r="0.85" fill="currentColor" />
        <circle cx="19.25" cy="5.75" r="0.85" fill="currentColor" />

        <rect
          className="art_app_switch_window"
          x="3.25"
          y="12.25"
          width="18.5"
          height="16.5"
          rx="2.75"
        />
        <path d="M3.5 17.25H21.5" />
        <circle cx="6.25" cy="14.75" r="0.85" fill="currentColor" />
        <circle cx="9.25" cy="14.75" r="0.85" fill="currentColor" />
        <circle cx="12.25" cy="14.75" r="0.85" fill="currentColor" />

        <path d="M2.25 9.5V7.75A4.5 4.5 0 0 1 6.75 3.25H8.25" />
        <path d="M5.9 1.25L8.45 3.25L5.9 5.25" />
        <path d="M29.75 22.5V24.25A4.5 4.5 0 0 1 25.25 28.75H23.75" />
        <path d="M26.1 26.75L23.55 28.75L26.1 30.75" />
      </svg>
    </span>
  );
}

export default AdminLayout;
